import clsx from 'clsx';
import { forwardRef, type FunctionComponent, type ReactElement, isValidElement } from 'react';

import { Typography } from '../typography/Typography';

type Props = {
  children: ReactElement<any>;
  iconLeft?: FunctionComponent;
  iconRight?: FunctionComponent;
  size?: 'default' | 'small';
  isOnDark?: boolean;
  fullWidth?: boolean;
  warning?: boolean;
  forceEnabled?: boolean;
  disableIconAnimation?: boolean;
  testid?: string;
};

/**
 * Een tekst link component.
 *
 * Wordt gebruikt voor de Terug knoppen bovenaan pagina's. Of voor het linken naar andere pagina's.
 */
export const TextLink = forwardRef((
  {
    children,
    iconLeft: IconLeft,
    iconRight: IconRight,
    size = 'default',
    isOnDark = false,
    fullWidth = false,
    forceEnabled = false,
    disableIconAnimation = false,
    warning,
    testid,
  }: Props,
  ref,
) => {
  const text = isValidElement(children) ? (children.props as any).children : '';

  const isDisabled =
    ((children.type === 'a' && !children?.props?.href)
      || (children.type === 'button' && children.props.disabled)
    )
    && !forceEnabled;

  const wrapperClasses = clsx([
    'group flex w-fit items-center',
    'active:!text-textlink-activated-on-light',
    'active:!no-underline',
  ], {
    'underline-offset-4 hover:underline': !isDisabled,
    'text-textlink-enabled-on-light': !isDisabled && !isOnDark && !warning,
    'text-textlink-enabled-on-dark': !isDisabled && isOnDark,
    'text-textlink-disabled-on-light': isDisabled && !isOnDark,
    'text-textlink-disabled-on-dark': isDisabled && isOnDark,
    'w-full justify-between': fullWidth,
    'text-error': warning,
  });

  const iconLeftClasses = clsx(
    'mr-2 shrink-0 transition-transform',
    'group-active:!text-textlink-activated-icon-on-light',
    size === 'default' && 'size-6',
    size === 'small' && 'size-[1.375rem]',
    !isDisabled && !disableIconAnimation && 'group-hover:-translate-x-1',
  );

  const iconRightClasses = clsx(
    'ml-2 shrink-0 transition-transform',
    'group-active:!text-textlink-activated-icon-on-light',
    size === 'default' && 'size-6',
    size === 'small' && 'size-[1.375rem]',
    !isDisabled && !disableIconAnimation && 'group-hover:translate-x-1',
  );

  return (
    <children.type
      {...(children.props as any)}
      ref={ref}
      className={wrapperClasses}
      data-testid={testid}
    >
      {IconLeft && (
        <span className={iconLeftClasses}>
          <IconLeft />
        </span>
      )}
      <Typography
        type="span"
        variant={size === 'default' ? 'label-large' : 'label-regular'}
      >
        {text}
      </Typography>
      {IconRight && (
        <span className={iconRightClasses}>
          <IconRight />
        </span>
      )}
    </children.type>
  );
});

TextLink.displayName = 'TextLink';
